import React, { useState, useEffect, useCallback, useRef } from 'react';
import './AdminProgramManagement.css';
import { validateRequired, validateMinLength, sanitizeInput } from '../utils/validation';
import config from '../config';

const { API_URL } = config;

const AdminProgramManagement = ({ user, onBack, onMessage }) => {
  // State for program form
  const [programForm, setProgramForm] = useState({
    name: '',
    title: '',
    description: '',
    assessment_name: '',
    requirements: [],
    status: 'active',
    manager_email: '',
    enrollment_email_sender: '',
    enrollment_email_subject: '',
    enrollment_email_body: '',
    enrollment_email_cc: ''
  });

  // State for data management
  const [programs, setPrograms] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedProgramForEdit, setSelectedProgramForEdit] = useState('');
  const [newRequirement, setNewRequirement] = useState('');
  const [showEnrollmentAnalytics, setShowEnrollmentAnalytics] = useState(false);

  // Refs to prevent duplicate API calls
  const loadingRef = useRef(false);

  // Load programs from API
  const loadPrograms = useCallback(async () => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/admin/programs`);
      if (!response.ok) {
        // If programs endpoint doesn't exist, return empty array
        if (response.status === 404) {
          setPrograms([]);
          return;
        }
        throw new Error(`HTTP ${response.status}`);
      }
      
      const programsData = await response.json();
      setPrograms(programsData || []);
    } catch (error) {
      console.error('Error loading programs:', error);
      onMessage({ text: 'Error loading programs: ' + error.message, type: 'error' });
      setPrograms([]);
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [onMessage]);

  // Load assessments for dropdown
  const loadAssessments = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/admin/questions?type=assessments`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      
      const assessmentsData = await response.json();
      setAssessments(assessmentsData || []);
    } catch (error) {
      console.error('Error loading assessments:', error);
      onMessage({ text: 'Error loading assessments: ' + error.message, type: 'error' });
      setAssessments([]);
    }
  }, [onMessage]);

  // Load enrollment data for analytics
  const loadEnrollments = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/admin/enrollments`);
      if (!response.ok) {
        // If enrollments endpoint doesn't exist, return empty array
        if (response.status === 404) {
          setEnrollments([]);
          return;
        }
        throw new Error(`HTTP ${response.status}`);
      }
      
      const enrollmentsData = await response.json();
      setEnrollments(enrollmentsData || []);
    } catch (error) {
      console.error('Error loading enrollments:', error);
      // Don't show error for enrollments as it's optional
      setEnrollments([]);
    }
  }, []);

  // Load all data on component mount
  useEffect(() => {
    loadPrograms().catch(console.error);
    loadAssessments().catch(console.error);
    loadEnrollments().catch(console.error);
  }, [loadPrograms, loadAssessments, loadEnrollments]);

  // Validate program form
  const validateProgramForm = (form) => {
    const errors = {};

    // Validate required fields
    if (!validateRequired(form.name)) {
      errors.name = 'Program name is required';
    } else if (!validateMinLength(form.name, 3)) {
      errors.name = 'Program name must be at least 3 characters';
    }

    if (!validateRequired(form.title)) {
      errors.title = 'Program title is required';
    } else if (!validateMinLength(form.title, 5)) {
      errors.title = 'Program title must be at least 5 characters';
    }

    if (!validateRequired(form.description)) {
      errors.description = 'Program description is required';
    } else if (!validateMinLength(form.description, 10)) {
      errors.description = 'Program description must be at least 10 characters';
    }

    if (!validateRequired(form.assessment_name)) {
      errors.assessment_name = 'Associated assessment is required';
    }

    // Validate manager email if provided
    if (form.manager_email && form.manager_email.trim()) {
      // More robust email validation pattern (RFC 5322 compliant)
      const emailPattern = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
      if (!emailPattern.test(form.manager_email.trim())) {
        errors.manager_email = 'Invalid email format';
      }
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  };

  // Handle program selection for editing
  const handleProgramSelect = (programName) => {
    const program = programs.find(p => p.name === programName);
    if (program) {
      setProgramForm({
        name: program.name,
        title: program.title || '',
        description: program.description || '',
        assessment_name: program.assessment_name || '',
        requirements: program.requirements || [],
        status: program.status || 'active',
        manager_email: program.manager_email || '',
        enrollment_email_sender: program.enrollment_email_sender || '',
        enrollment_email_subject: program.enrollment_email_subject || '',
        enrollment_email_body: program.enrollment_email_body || '',
        enrollment_email_cc: program.enrollment_email_cc || ''
      });
    } else {
      // Clear form for new program
      setProgramForm({
        name: programName,
        title: '',
        description: '',
        assessment_name: '',
        requirements: [],
        status: 'active',
        manager_email: '',
        enrollment_email_sender: '',
        enrollment_email_subject: '',
        enrollment_email_body: '',
        enrollment_email_cc: ''
      });
    }
    setSelectedProgramForEdit(programName);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Sanitize inputs
    const sanitizedForm = {
      ...programForm,
      name: sanitizeInput(programForm.name),
      title: sanitizeInput(programForm.title),
      description: sanitizeInput(programForm.description),
      assessment_name: sanitizeInput(programForm.assessment_name),
      requirements: programForm.requirements.map(req => sanitizeInput(req)),
      manager_email: programForm.manager_email ? sanitizeInput(programForm.manager_email.trim()) : ''
    };

    // Validate form
    const validation = validateProgramForm(sanitizedForm);
    if (!validation.isValid) {
      const errorMessages = Object.values(validation.errors).join(', ');
      onMessage({ text: 'Validation Error: ' + errorMessages, type: 'error' });
      return;
    }

    setLoading(true);

    try {
      const programData = {
        ...sanitizedForm,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      const response = await fetch(`${API_URL}/admin/programs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(user?.token ? { 'Authorization': `Bearer ${user.token}` } : {})
        },
        body: JSON.stringify(programData)
      });

      const result = await response.json();

      if (response.ok) {
        onMessage({ text: 'Program saved successfully!', type: 'success' });
        setProgramForm({
          name: '',
          title: '',
          description: '',
          assessment_name: '',
          requirements: [],
          status: 'active',
          manager_email: '',
          enrollment_email_sender: '',
          enrollment_email_subject: '',
          enrollment_email_body: '',
          enrollment_email_cc: ''
        });
        setSelectedProgramForEdit('');
        loadPrograms();
      } else {
        const errorMsg = result.error || result.message || 'Unknown error occurred';
        onMessage({ text: 'Error: ' + errorMsg, type: 'error' });
      }
    } catch (error) {
      console.error('Program save error:', error);
      onMessage({ text: 'Error: ' + error.message, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Handle program deletion
  const handleDelete = async (programName, forceDelete = false) => {
    const confirmMessage = forceDelete 
      ? `Are you sure you want to FORCE DELETE the program "${programName}"? This will deactivate all existing enrollments and cannot be undone.`
      : `Are you sure you want to delete the program "${programName}"? This action cannot be undone.`;
      
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/admin/programs`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...(user?.token ? { 'Authorization': `Bearer ${user.token}` } : {})
        },
        body: JSON.stringify({ 
          name: programName,
          force_delete: forceDelete 
        })
      });

      const result = await response.json();

      if (response.ok) {
        onMessage({ text: 'Program deleted successfully!', type: 'success' });
        loadPrograms();
        // Clear form if deleted program was being edited
        if (selectedProgramForEdit === programName) {
          setProgramForm({
            name: '',
            title: '',
            description: '',
            assessment_name: '',
            requirements: [],
            status: 'active'
          });
          setSelectedProgramForEdit('');
        }
      } else if (response.status === 409) {
        // Handle enrollment conflict
        const enrollmentCount = result.enrollment_count || 0;
        const activeCount = result.active_enrollment_count || 0;
        
        const forceDeleteConfirm = window.confirm(
          `Cannot delete program: ${enrollmentCount} enrollments found (${activeCount} active).\n\n` +
          `Click OK to FORCE DELETE and deactivate all enrollments, or Cancel to abort.`
        );
        
        if (forceDeleteConfirm) {
          await handleDelete(programName, true);
        }
      } else {
        const errorMsg = result.error || result.message || 'Unknown error occurred';
        onMessage({ text: 'Error: ' + errorMsg, type: 'error' });
      }
    } catch (error) {
      console.error('Program delete error:', error);
      onMessage({ text: 'Error: ' + error.message, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Add requirement to the list
  const addRequirement = () => {
    if (!newRequirement.trim() || programForm.requirements.includes(newRequirement.trim())) {
      return;
    }
    setProgramForm({
      ...programForm,
      requirements: [...programForm.requirements, newRequirement.trim()]
    });
    setNewRequirement('');
  };

  // Remove requirement from the list
  const removeRequirement = (requirementToRemove) => {
    setProgramForm({
      ...programForm,
      requirements: programForm.requirements.filter(req => req !== requirementToRemove)
    });
  };

  // Calculate enrollment analytics
  const getEnrollmentAnalytics = () => {
    const analytics = {};
    
    programs.forEach(program => {
      const programEnrollments = enrollments.filter(e => e.program_name === program.name);
      analytics[program.name] = {
        totalEnrollments: programEnrollments.length,
        activeEnrollments: programEnrollments.filter(e => e.status === 'active').length,
        completedEnrollments: programEnrollments.filter(e => e.status === 'completed').length,
        inactiveEnrollments: programEnrollments.filter(e => e.status === 'inactive').length
      };
    });

    return analytics;
  };

  const enrollmentAnalytics = getEnrollmentAnalytics();

  return (
    <div className="admin-program-management">
      {/* Program Form */}
      <div className="card">
        <div className="card-header">
          <h3>Create/Update Program</h3>
          <button 
            type="button" 
            className="btn btn-secondary"
            onClick={() => setShowEnrollmentAnalytics(!showEnrollmentAnalytics)}
          >
            {showEnrollmentAnalytics ? 'Hide' : 'Show'} Analytics
          </button>
        </div>

        <form onSubmit={handleSubmit} className="program-form">
          <div className="form-row">
            <div className="form-group">
              <label>Program Name:</label>
              <div className="input-group">
                <select
                  value={selectedProgramForEdit}
                  onChange={(e) => handleProgramSelect(e.target.value)}
                  className="form-control"
                >
                  <option value="">Select existing or create new...</option>
                  {programs.map(program => (
                    <option key={program.name} value={program.name}>
                      {program.name}
                    </option>
                  ))}
                </select>
                <input
                  type="text"
                  value={programForm.name}
                  onChange={(e) => {
                    setProgramForm({...programForm, name: e.target.value});
                    setSelectedProgramForEdit('');
                  }}
                  placeholder="Or type new program name"
                  className="form-control"
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label>Status:</label>
              <select
                value={programForm.status}
                onChange={(e) => setProgramForm({...programForm, status: e.target.value})}
                className="form-control"
                required
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Program Title:</label>
            <input
              type="text"
              value={programForm.title}
              onChange={(e) => setProgramForm({...programForm, title: e.target.value})}
              placeholder="Enter program display title"
              className="form-control"
              required
            />
          </div>

          <div className="form-group">
            <label>Description:</label>
            <textarea
              value={programForm.description}
              onChange={(e) => setProgramForm({...programForm, description: e.target.value})}
              placeholder="Enter program description"
              className="form-control"
              rows="4"
              required
            />
          </div>

          <div className="form-group">
            <label>Associated Assessment:</label>
            <select
              value={programForm.assessment_name}
              onChange={(e) => setProgramForm({...programForm, assessment_name: e.target.value})}
              className="form-control"
              required
            >
              <option value="">Select Assessment...</option>
              {assessments.map(assessment => (
                <option key={assessment.name} value={assessment.name}>
                  {assessment.name}
                </option>
              ))}
            </select>
            {assessments.length === 0 && (
              <p className="help-text">No assessments available. Create assessments first in Assessment Management.</p>
            )}
          </div>

          <div className="form-group">
            <label>Program Manager Email:</label>
            <input
              type="email"
              value={programForm.manager_email}
              onChange={(e) => setProgramForm({...programForm, manager_email: e.target.value})}
              placeholder="manager@example.com (optional)"
              className="form-control"
            />
            <p className="help-text">
              📧 Receives email notifications when users enroll in this program. Leave empty for no notifications.
            </p>
          </div>

          <div className="form-group">
            <label>Enrollment Email - Sender:</label>
            <input
              type="email"
              value={programForm.enrollment_email_sender}
              onChange={(e) => setProgramForm({...programForm, enrollment_email_sender: e.target.value})}
              placeholder="noreply@enpro.com (optional, uses default if empty)"
              className="form-control"
            />
            <p className="help-text">
              📤 From address for the enrollment confirmation email sent to the user. Must be a verified SES address.
            </p>
          </div>

          <div className="form-group">
            <label>Enrollment Email - Subject:</label>
            <input
              type="text"
              value={programForm.enrollment_email_subject}
              onChange={(e) => setProgramForm({...programForm, enrollment_email_subject: e.target.value})}
              placeholder="Welcome to {program_title}! (optional)"
              className="form-control"
            />
            <p className="help-text">
              📝 Subject line for the enrollment confirmation email. Leave empty to disable user enrollment emails.
            </p>
          </div>

          <div className="form-group">
            <label>Enrollment Email - Message:</label>
            <textarea
              value={programForm.enrollment_email_body}
              onChange={(e) => setProgramForm({...programForm, enrollment_email_body: e.target.value})}
              placeholder="Dear {user_email}, welcome to {program_title}! You are now enrolled and can start your assessment..."
              className="form-control"
              rows="6"
            />
            <p className="help-text">
              ✉️ Message body sent to the user upon enrollment. Available placeholders: <code>{'{user_email}'}</code>, <code>{'{program_title}'}</code>, <code>{'{program_name}'}</code>, <code>{'{enrollment_date}'}</code>. Leave empty to disable.
            </p>
          </div>

          <div className="form-group">
            <label>Enrollment Email - CC:</label>
            <input
              type="text"
              value={programForm.enrollment_email_cc}
              onChange={(e) => setProgramForm({...programForm, enrollment_email_cc: e.target.value})}
              placeholder="cc@example.com, cc2@example.com (optional, comma-separated)"
              className="form-control"
            />
            <p className="help-text">
              📋 Email address(es) to put in copy of the enrollment confirmation. Separate multiple addresses with commas.
            </p>
          </div>

          <div className="form-group">
            <label>Program Pathway:</label>
            <div className="requirements-manager">
              <div className="input-group">
                <input
                  type="text"
                  value={newRequirement}
                  onChange={(e) => setNewRequirement(e.target.value)}
                  placeholder="Add pathway step (e.g., Complete AWS Certified Cloud Practitioner)"
                  className="form-control"
                />
                <button 
                  type="button" 
                  onClick={addRequirement}
                  className="btn btn-primary"
                  disabled={!newRequirement.trim()}
                >
                  Add
                </button>
              </div>
              
              {programForm.requirements.length > 0 && (
                <div className="requirements-list">
                  <strong>Pathway Steps:</strong>
                  <ul>
                    {programForm.requirements.map((requirement, index) => (
                      <li key={index} className="requirement-item">
                        <span>{requirement}</span>
                        <button 
                          type="button"
                          onClick={() => removeRequirement(requirement)}
                          className="btn btn-danger"
                        >
                          Remove
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          <div className="form-actions">
            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={loading || assessments.length === 0}
            >
              {loading ? 'Saving...' : 'Save Program'}
            </button>
            <button 
              type="button" 
              className="btn btn-secondary" 
              onClick={() => {
                setProgramForm({
                  name: '',
                  title: '',
                  description: '',
                  assessment_name: '',
                  requirements: [],
                  status: 'active',
                  manager_email: '',
                  enrollment_email_sender: '',
                  enrollment_email_subject: '',
                  enrollment_email_body: '',
                  enrollment_email_cc: ''
                });
                setSelectedProgramForEdit('');
              }}
            >
              Clear Form
            </button>
          </div>
        </form>
      </div>

      {/* Enrollment Analytics */}
      {showEnrollmentAnalytics && (
        <div className="card">
          <h3>Enrollment Analytics</h3>
          {programs.length === 0 ? (
            <p>No programs available for analytics.</p>
          ) : (
            <div className="analytics-grid">
              {programs.map(program => (
                <div key={program.name} className="analytics-card">
                  <h4>{program.title || program.name}</h4>
                  <div className="analytics-stats">
                    <div className="stat">
                      <span className="stat-label">Total Enrollments:</span>
                      <span className="stat-value">{enrollmentAnalytics[program.name]?.totalEnrollments || 0}</span>
                    </div>
                    <div className="stat">
                      <span className="stat-label">Active:</span>
                      <span className="stat-value active">{enrollmentAnalytics[program.name]?.activeEnrollments || 0}</span>
                    </div>
                    <div className="stat">
                      <span className="stat-label">Completed:</span>
                      <span className="stat-value completed">{enrollmentAnalytics[program.name]?.completedEnrollments || 0}</span>
                    </div>
                    <div className="stat">
                      <span className="stat-label">Inactive:</span>
                      <span className="stat-value inactive">{enrollmentAnalytics[program.name]?.inactiveEnrollments || 0}</span>
                    </div>
                  </div>
                  <div className="program-info">
                    <p><strong>Assessment:</strong> {program.assessment_name}</p>
                    <p><strong>Status:</strong> <span className={`status ${program.status}`}>{program.status}</span></p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Existing Programs List */}
      <div className="card">
        <div className="card-header">
          <h3>Existing Programs</h3>
          <button 
            className="btn btn-secondary" 
            onClick={() => loadPrograms().catch(console.error)} 
            disabled={loading}
          >
            {loading ? 'Loading...' : 'Refresh Programs'}
          </button>
        </div>

        {loading && <div className="loading">Loading programs...</div>}

        {!loading && (
          <div className="programs-list">
            {programs.length === 0 ? (
              <p>No programs configured. Create your first program using the form above.</p>
            ) : (
              programs.map(program => (
                <div key={program.name} className="program-item">
                  <div className="program-header">
                    <h4>{program.title || program.name}</h4>
                    <div className="program-actions">
                      <button 
                        className="btn btn-secondary" 
                        onClick={() => handleProgramSelect(program.name)}
                      >
                        Edit
                      </button>
                      <button 
                        className="btn btn-danger" 
                        onClick={() => handleDelete(program.name)}
                        disabled={loading}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                  
                  <div className="program-details">
                    <p><strong>Name:</strong> {program.name}</p>
                    <p><strong>Description:</strong> {program.description}</p>
                    <p><strong>Assessment:</strong> {program.assessment_name}</p>
                    <p><strong>Status:</strong> <span className={`status ${program.status}`}>{program.status}</span></p>
                    {program.manager_email && (
                      <p><strong>Manager Email:</strong> 📧 {program.manager_email}</p>
                    )}
                    
                    {program.requirements && program.requirements.length > 0 && (
                      <div className="program-requirements">
                        <strong>Program Pathway:</strong>
                        <ul>
                          {program.requirements.map((requirement, index) => (
                            <li key={index}>{requirement}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {enrollmentAnalytics[program.name] && (
                      <div className="program-stats">
                        <strong>Enrollments:</strong> {enrollmentAnalytics[program.name].totalEnrollments} total 
                        ({enrollmentAnalytics[program.name].activeEnrollments} active, {enrollmentAnalytics[program.name].completedEnrollments} completed)
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminProgramManagement;